import React, { useState, useEffect, useCallback } from 'react';
import { groupService, Group, User } from '../services/groupService';
import { sharingService, ShareInfo, ShareTarget, SharePermission } from '../services/sharingService';
import UserAutocomplete from './UserAutocomplete';

interface ShareCustomerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  customerName: string;
}

type TabType = 'users' | 'groups';

const ShareCustomerDialog: React.FC<ShareCustomerDialogProps> = ({
  isOpen,
  onClose,
  customerId,
  customerName,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('users');
  const [groups, setGroups] = useState<Group[]>([]);
  const [currentShares, setCurrentShares] = useState<ShareInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Users tab state
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);

  // Groups tab state
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(new Set());

  // Permission to grant when sharing (applies to whole batch)
  const [pendingPermission, setPendingPermission] = useState<SharePermission>('viewer');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [groupsData, sharesData] = await Promise.all([
        groupService.getGroups(),
        sharingService.getCustomerShares(customerId),
      ]);
      setGroups(groupsData);
      setCurrentShares(sharesData);
      setError('');
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (isOpen) {
      fetchData();
      setSelectedUsers([]);
      setSelectedGroupIds(new Set());
      setSuccessMessage('');
    }
  }, [isOpen, fetchData]);

  const handleShare = async () => {
    const sharesToAdd: ShareTarget[] = [];

    // Add selected users
    selectedUsers.forEach((user) => {
      sharesToAdd.push({ type: 'user', id: user.id, permission: pendingPermission });
    });

    // Add selected groups
    selectedGroupIds.forEach((groupId) => {
      sharesToAdd.push({ type: 'group', id: groupId, permission: pendingPermission });
    });

    if (sharesToAdd.length === 0) {
      setError('Please select users or groups to share with');
      return;
    }

    setSharing(true);
    setError('');
    try {
      const result = await sharingService.shareCustomer(customerId, sharesToAdd);

      if (result.shared.length > 0) {
        setSuccessMessage(`Shared with ${result.shared.map(s => s.name).join(', ')} as ${pendingPermission}`);
      }

      if (result.alreadyShared.length > 0) {
        setError(`Already shared with ${result.alreadyShared.length} item(s)`);
      }

      setSelectedUsers([]);
      setSelectedGroupIds(new Set());
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to share customer');
    } finally {
      setSharing(false);
    }
  };

  const handleRevokeShare = async (shareId: string) => {
    try {
      await sharingService.revokeShare(customerId, shareId);
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to revoke share');
    }
  };

  const handleChangePermission = async (shareId: string, permission: SharePermission) => {
    try {
      await sharingService.updateSharePermission(customerId, shareId, permission);
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to update permission');
    }
  };

  const toggleGroupSelection = (groupId: string) => {
    const newSelection = new Set(selectedGroupIds);
    if (newSelection.has(groupId)) {
      newSelection.delete(groupId);
    } else {
      newSelection.add(groupId);
    }
    setSelectedGroupIds(newSelection);
  };

  // Get IDs of users and groups that already have access
  const sharedUserIds = currentShares
    .filter(s => s.shareType === 'individual')
    .map(s => s.sharedWith.id);
  const sharedGroupIds = currentShares
    .filter(s => s.shareType === 'group')
    .map(s => s.sharedWith.id);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75" onClick={onClose} />

        {/* Modal */}
        <div className="inline-block overflow-hidden text-left align-bottom transition-all transform bg-white dark:bg-gray-800 rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Share Customer
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {customerName}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Messages */}
          {error && (
            <div className="mx-6 mt-4 px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}
          {successMessage && (
            <div className="mx-6 mt-4 px-4 py-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-600 dark:text-green-400 text-sm">
              {successMessage}
            </div>
          )}

          {/* Tabs */}
          <div className="px-6 pt-4">
            <div className="flex border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setActiveTab('users')}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                  activeTab === 'users'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                Share with Users
              </button>
              <button
                onClick={() => setActiveTab('groups')}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                  activeTab === 'groups'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                Share with Groups
              </button>
            </div>
          </div>

          {/* Content */}
          <div className={`px-6 py-4 ${activeTab === 'groups' ? 'max-h-[50vh] overflow-y-auto' : 'overflow-visible'}`}>
            {loading ? (
              <div className="flex justify-center py-8">
                <svg className="animate-spin h-8 w-8 text-blue-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            ) : (
              <>
                {/* Users Tab */}
                {activeTab === 'users' && (
                  <div className="space-y-4 min-h-[200px]">
                    <UserAutocomplete
                      selectedUsers={selectedUsers}
                      onSelect={setSelectedUsers}
                      excludeUserIds={sharedUserIds}
                      placeholder="Search by name or email..."
                    />
                  </div>
                )}

                {/* Groups Tab */}
                {activeTab === 'groups' && (
                  <div className="space-y-2">
                    {groups.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                        No groups available. Create a group first to share with teams.
                      </p>
                    ) : (
                      groups.map((group) => {
                        const isAlreadyShared = sharedGroupIds.includes(group.id);
                        const isSelected = selectedGroupIds.has(group.id);

                        return (
                          <label
                            key={group.id}
                            className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                              isAlreadyShared
                                ? 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed'
                                : isSelected
                                ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700'
                                : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected || isAlreadyShared}
                              disabled={isAlreadyShared}
                              onChange={() => !isAlreadyShared && toggleGroupSelection(group.id)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <div className="ml-3 flex-1">
                              <div className="font-medium text-gray-900 dark:text-white text-sm">
                                {group.name}
                                {isAlreadyShared && (
                                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(Already shared)</span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {group.memberCount} member{group.memberCount !== 1 ? 's' : ''}
                              </div>
                            </div>
                          </label>
                        );
                      })
                    )}
                  </div>
                )}

                {/* Permission picker for new shares */}
                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Permission level for new shares
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setPendingPermission('viewer')}
                      className={`flex-1 px-3 py-2 text-sm rounded-lg border ${
                        pendingPermission === 'viewer'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                          : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="font-medium">Viewer</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Can view + create their own reports</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingPermission('editor')}
                      className={`flex-1 px-3 py-2 text-sm rounded-lg border ${
                        pendingPermission === 'editor'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                          : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="font-medium">Editor</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Can edit, delete projects/reports</div>
                    </button>
                  </div>
                </div>

                {/* Current Shares */}
                {currentShares.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Currently Shared With
                    </h4>
                    <div className="space-y-2">
                      {currentShares.map((share) => (
                        <div
                          key={share.id}
                          className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {share.shareType === 'individual' ? (
                              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                            )}
                            <div className="min-w-0">
                              <span className="text-sm text-gray-900 dark:text-white truncate">
                                {share.sharedWith.name}
                              </span>
                              {share.sharedWith.email && (
                                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                  {share.sharedWith.email}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <select
                              value={share.permission}
                              onChange={(e) => handleChangePermission(share.id, e.target.value as SharePermission)}
                              className="text-xs px-2 py-1 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                              title="Change permission"
                            >
                              <option value="viewer">Viewer</option>
                              <option value="editor">Editor</option>
                            </select>
                            <button
                              onClick={() => handleRevokeShare(share.id)}
                              className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                              title="Revoke access"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleShare}
              disabled={sharing || (selectedUsers.length === 0 && selectedGroupIds.size === 0)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sharing ? 'Sharing...' : 'Share'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareCustomerDialog;
